import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  Shield, 
  Share2, 
  User, 
  Video, 
  FolderPlus, 
  Lock, 
  Library, 
  CreditCard,
  CheckCircle2,
  Loader2 
} from 'lucide-react';

interface VideoInfo {
  title: string;
  ownerName: string;
  ownerId: string;
}

interface ShareAcceptCardProps {
  videoInfo: VideoInfo;
  isProcessing: boolean;
  onAccept: () => void;
  onDecline: () => void;
}

export function ShareAcceptCard({
  videoInfo,
  isProcessing,
  onAccept,
  onDecline,
}: ShareAcceptCardProps) {
  useLanguage();
  const [agreed, setAgreed] = useState(false);

  const handleAccept = () => {
    if (!agreed || isProcessing) return;
    onAccept();
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.4, ease: 'easeOut' }}
      className="w-full bg-white rounded-2xl shadow-[0_12px_30px_rgba(0,0,0,0.10),0_2px_8px_rgba(0,0,0,0.06)] border border-[rgba(20,40,90,0.08)] overflow-hidden"
    >
      <div className="p-6">
        {/* Trust Badge */}
        <div className="flex justify-center mb-4">
          <span className="text-xs text-slate-500 flex items-center gap-1.5 bg-slate-50 px-3 py-1.5 rounded-full">
            <Shield className="w-3.5 h-3.5" />
            {translateInterfaceText("Link verificado • Seguro")}</span>
        </div>

        {/* Icon + Header */}
        <div className="text-center mb-[18px]">
          <div className="relative w-14 h-14 mx-auto mb-2.5">
            <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
              <Share2 className="w-7 h-7 text-primary" />
            </div>
          </div>
          <h1 className="text-[20px] sm:text-[22px] font-bold text-foreground leading-tight">
            {translateInterfaceText("Um vídeo foi compartilhado com você")}</h1>
          <p className="text-[13px] text-muted-foreground mt-1.5 max-w-[400px] mx-auto">
            {translateInterfaceText("Confirme abaixo para adicionar o vídeo à sua conta HostVSL.")}</p>
        </div>

        {/* Info Card (grid 2 cols) */}
        <div className="bg-[#F7FAFF] border border-[rgba(30,80,200,0.10)] rounded-xl p-[14px_16px] mb-4 relative">
          {/* Badge oficial - azul
          <div className="absolute top-2.5 right-2.5">
            <span className="text-[11px] bg-blue-50 text-blue-600 px-2.5 py-1 rounded-full font-medium flex items-center gap-1">
              <CheckCircle2 className="w-3 h-3" />
              Compartilhamento oficial
            </span>
          </div> */}
          
          {/* Grid 2 colunas desktop, 1 coluna mobile */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-[13px] mt-6 sm:mt-0">
            <InfoRow 
              icon={User} 
              label={translateInterfaceText("Compartilhado por")}
              value={videoInfo.ownerName || `ID #${videoInfo.ownerId}`} 
            />
            <InfoRow 
              icon={FolderPlus} 
              label={translateInterfaceText("Destino")}
              value="Meus Vídeos" 
            />
          </div>
          <div className="text-[13px] mt-2.5 pt-2.5 border-t border-[rgba(30,80,200,0.08)]">
            <InfoRow 
              icon={Video} 
              label={translateInterfaceText("Vídeo")}
              value={videoInfo.title} 
            />
          </div>
        </div>

        {/* Rules (3 mini cards) */}
        <div className="space-y-2.5 mb-4">
          <RuleCard 
            icon={Lock}
            title={translateInterfaceText("Sem redistribuição")}
            description={translateInterfaceText("Você não poderá compartilhar este vídeo com outras pessoas.")}
          />
          <RuleCard 
            icon={Library}
            title={translateInterfaceText("Vai para sua biblioteca")}
            description={translateInterfaceText("Ficará disponível como um vídeo enviado por você.")}
          />
          <RuleCard 
            icon={CreditCard}
            title={translateInterfaceText("Consumo no seu plano")}
            description={translateInterfaceText("Os plays serão consumidos do seu plano.")}
          />
        </div>

        {/* Checkbox Callout */}
        <div className="bg-[rgba(0,80,255,0.04)] border border-[rgba(0,80,255,0.10)] rounded-[10px] p-3 mb-4">
          <label className="flex items-start gap-3 cursor-pointer select-none group">
            <Checkbox 
              checked={agreed}
              onCheckedChange={(checked) => setAgreed(checked === true)}
              className="mt-0.5"
            />
            <div>
              <span className="text-[13px] font-medium text-foreground group-hover:text-primary transition-colors">
                {translateInterfaceText("Li e concordo com as regras acima")}</span>
              <p className="text-[12px] text-muted-foreground mt-0.5">
                {translateInterfaceText("Se não concordar, você pode recusar e nada será adicionado.")}</p>
            </div>
          </label>
        </div>

        {/* Processing micro status */}
        {isProcessing && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            className="flex items-center justify-center gap-2 text-sm text-muted-foreground py-2 mb-3"
          >
            <Loader2 className="h-4 w-4 animate-spin text-primary" />
            <span>{translateInterfaceText("Criando cópia do vídeo na sua conta...")}</span>
          </motion.div>
        )}

        {/* Buttons (right-aligned desktop, stacked mobile) */}
        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2.5">
          <Button
            variant="outline"
            onClick={onDecline}
            disabled={isProcessing}
            className="h-11 sm:w-auto hover:bg-muted hover:text-[#020817]"
          >
            {translateInterfaceText("Recusar")}</Button>
          <Button
            onClick={handleAccept}
            disabled={!agreed || isProcessing}
            className="h-11 sm:min-w-[220px] bg-[#4B7BFF] hover:bg-[#3B6BEF] text-white font-medium"
          >
            {isProcessing ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                {translateInterfaceText("Adicionando...")}</>
            ) : (
              translateInterfaceText("Aceitar e adicionar")
            )}
          </Button>
        </div>
      </div>
    </motion.div>
  );
}

function InfoRow({ 
  icon: Icon, 
  label, 
  value 
}: { 
  icon: React.ElementType; 
  label: string; 
  value: string; 
}) {
  useLanguage();
  return (
    <div className="flex items-center gap-2 text-[13px]">
      <Icon className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
      <span className="text-muted-foreground">{label}:</span>
      <span className="font-medium text-foreground truncate">{value}</span>
    </div>
  );
}

function RuleCard({ 
  icon: Icon, 
  title, 
  description 
}: { 
  icon: React.ElementType; 
  title: string; 
  description: string;
}) {
  useLanguage();
  return (
    <div className="flex items-start gap-3 bg-slate-50/80 border border-slate-100 rounded-lg p-3">
      <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
        <Icon className="h-[18px] w-[18px] text-primary" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[13px] font-semibold text-foreground">{title}</p>
        <p className="text-[12px] text-muted-foreground mt-0.5 leading-relaxed">{description}</p>
      </div>
    </div>
  );
}
